<template>
  <div class="wechat-login">
    <!-- 加载状态 -->
    <div v-if="loading && !qrCodeData" class="loading-container">
      <el-icon class="loading-icon"><Loading /></el-icon>
      <p>正在生成二维码...</p>
    </div>

    <!-- 二维码显示 -->
    <div v-else-if="qrCodeData && status === 'pending'" class="qr-code-container">
      <div class="qr-code-wrapper">
        <img :src="qrCodeData.qrcodeUrl" alt="微信登录二维码" class="qr-code" />
        <div v-if="refreshing" class="qr-overlay">
          <el-icon class="loading-icon"><Loading /></el-icon>
        </div>
      </div>
      <p class="tip">请使用微信扫描二维码登录</p>
      <div class="qr-actions">
        <TButton size="small" @click="refreshQRCode" :loading="refreshing" variant="secondary">
          <el-icon><Refresh /></el-icon>
          刷新二维码
        </TButton>
      </div>
      <div class="countdown" v-if="countdown > 0">
        {{ Math.floor(countdown / 60) }}:{{ String(countdown % 60).padStart(2, '0') }} 后过期
      </div>
    </div>

    <!-- 扫码成功状态 -->
    <div v-else-if="status === 'scanned'" class="scanned-container">
      <div class="success-icon">
        <el-icon><SuccessFilled /></el-icon>
      </div>
      <p class="status-text">扫码成功</p>
      <p class="tip">请在手机上确认登录</p>
      <div v-if="userInfo" class="user-preview">
        <el-avatar :size="48" :src="userInfo.head_img_url">
          {{ userInfo.nickname?.charAt(0) }}
        </el-avatar>
        <p class="nickname">{{ userInfo.nickname }}</p>
      </div>
    </div>

    <!-- 登录成功状态 -->
    <div v-else-if="status === 'confirmed'" class="confirmed-container">
      <div class="success-icon">
        <el-icon><CircleCheckFilled /></el-icon>
      </div>
      <p class="status-text">登录成功</p>
      <p class="tip">正在跳转...</p>
    </div>

    <!-- 过期状态 -->
    <div v-else-if="status === 'expired'" class="expired-container">
      <div class="error-icon">
        <el-icon><WarningFilled /></el-icon>
      </div>
      <p class="status-text">二维码已过期</p>
      <TButton variant="primary" @click="refreshQRCode" :loading="refreshing">
        重新获取
      </TButton>
    </div>

    <!-- 功能未启用状态 -->
    <div v-else-if="status === 'disabled'" class="disabled-container">
      <div class="disabled-content">
        <div class="disabled-icon">
          <el-icon><Lock /></el-icon>
        </div>
        <h3 class="disabled-title">微信登录功能暂未开放</h3>
        <p class="disabled-desc">
          管理员尚未启用微信扫码登录功能<br>
          您可以使用邮箱账号登录系统
        </p>
        <div class="disabled-actions">
          <TButton variant="primary" @click="$emit('switchToEmail')" size="large">
            <el-icon><Message /></el-icon>
            使用邮箱登录
          </TButton>
        </div>
        <div class="disabled-footer">
          <p class="contact-info">
            如需开启微信登录，请联系系统管理员
          </p>
        </div>
      </div>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <el-alert
        :title="error"
        type="error"
        :closable="false"
        show-icon
      />
      <TButton variant="primary" @click="initQRCode" style="margin-top: 12px;">
        重试
      </TButton>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, onUnmounted, onActivated, computed } from 'vue';
import { ElMessage } from 'element-plus';
import {
  Loading,
  Refresh,
  SuccessFilled,
  CircleCheckFilled,
  WarningFilled,
  Lock,
  Message
} from '@element-plus/icons-vue';
import { useUserStore } from '@/store';
import { TButton } from '@/components/ui';

// Props
const props = defineProps({
  onSuccess: {
    type: Function,
    default: () => {}
  },
  onError: {
    type: Function,
    default: () => {}
  }
});

// Emits
const emit = defineEmits(['switchToEmail']);

// Store
const userStore = useUserStore();

// 响应式数据
const loading = ref(false);
const refreshing = ref(false);
const qrCodeData = ref(null);
const status = ref('pending');
const userInfo = ref(null);
const error = ref('');
const countdown = ref(0);
const isPolling = ref(false); // 轮询状态标志

// 轮询相关
let pollTimer = null;
let countdownTimer = null;
let shouldPoll = false; // 控制是否应该继续轮询的标志位

// 计算属性
const baseURL = computed(() => {
  // 根据环境获取API基础URL
  return import.meta.env.VITE_BASE_URL || 'http://localhost:8080';
});

// 方法
const initQRCode = async () => {
  try {
    loading.value = true;
    error.value = '';
    status.value = 'pending';
    userInfo.value = null;

    const response = await fetch(`/api/v1/auth/wechat/qrcode`);

    // 检查HTTP状态码
    if (!response.ok) {
      let errorMessage = `请求失败 (${response.status})`;
      let isDisabled = false;

      try {
        const errorResult = await response.json();
        errorMessage = errorResult.message || errorMessage;
        // 检查是否是功能未启用
        if (errorMessage.includes('微信登录功能未启用') ||
            errorMessage.includes('微信登录未启用')) {
          isDisabled = true;
        }
      } catch {
        // 如果响应不是JSON格式，使用默认错误信息
        if (response.status === 304) {
          isDisabled = true;
        }
      }

      if (isDisabled) {
        status.value = 'disabled';
        return;
      }

      throw new Error(errorMessage);
    }

    const result = await response.json();

    // 后端返回格式: { code: 0, message: "success", data: {...} }
    // code === 0 表示成功
    if (result.code === 0 && result.data) {
      qrCodeData.value = result.data;
      startPolling();
      startCountdown();
    } else {
      throw new Error(result.message || '获取二维码失败');
    }
  } catch (err) {
    error.value = err.message || '获取二维码失败，请重试';
    props.onError(error.value, err);
  } finally {
    loading.value = false;
  }
};

const refreshQRCode = async () => {
  try {
    refreshing.value = true;
    stopPolling();
    stopCountdown();
    await initQRCode();
  } catch (err) {
    error.value = '刷新二维码失败，请重试';
  } finally {
    refreshing.value = false;
  }
};

const startPolling = () => {
  if (!qrCodeData.value) return;

  // 如果已经在轮询，先停止
  if (pollTimer) {
    stopPolling();
  }

  isPolling.value = true;

  pollTimer = setInterval(async () => {
    await checkQRCodeStatus();
  }, 2000); // 每2秒轮询一次
};

const stopPolling = () => {
  isPolling.value = false;

  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const startCountdown = () => {
  if (!qrCodeData.value) return;
  
  countdown.value = 300; // 5分钟倒计时
  countdownTimer = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      stopCountdown();
      status.value = 'expired';
      stopPolling();
    }
  }, 1000);
};

const stopCountdown = () => {
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
};

const checkQRCodeStatus = async () => {
  // 首先检查是否应该继续轮询
  if (!isPolling.value) {
    return;
  }

  if (!qrCodeData.value) {
    return;
  }

  try {
    const response = await fetch(
      `/api/v1/auth/wechat/qrcode/status?sceneStr=${qrCodeData.value.sceneStr}`
    );

    // 再次检查是否应该继续（防止在请求过程中状态改变）
    if (!isPolling.value) {
      return;
    }

    // 检查HTTP状态码
    if (!response.ok) {
      console.error('查询二维码状态失败:', response.status);
      return;
    }

    const result = await response.json();

    // 后端返回格式: { code: 0, message: "success", data: {...} }
    // 字段名使用 camelCase（符合字段命名统一规范）
    if (result.code === 0 && result.data) {
      const newStatus = result.data.status;
      status.value = newStatus;

      if (result.data.userInfo) {
        userInfo.value = result.data.userInfo;
      }

      switch (newStatus) {
        case 'pending':
          // 继续轮询
          break;
        case 'scanned':
          // 已扫码，等待确认
          break;
        case 'confirmed':
          // 登录成功
          stopPolling();
          stopCountdown();
          if (result.data.token) {
            // 保存token和用户信息到localStorage（与login方法保持一致）
            localStorage.setItem('auth_token', result.data.token);

            // 后端返回的是 userInfo 字段
            if (result.data.userInfo) {
              localStorage.setItem('user_info', JSON.stringify(result.data.userInfo));
            }

            // 恢复用户状态到store
            userStore.restoreUserState();

            // 获取用户套餐信息
            await userStore.fetchUserPackage();

            // 延迟1.5秒后调用成功回调，让用户看到"登录成功 正在跳转..."的提示
            setTimeout(() => {
              props.onSuccess(result.data);
            }, 1500);
          }
          break;
        case 'expired':
          // 已过期
          stopPolling();
          stopCountdown();
          break;
      }
    }
  } catch (err) {
    console.error('检查二维码状态失败:', err);
  }
};

// 生命周期
onMounted(() => {
  initQRCode();
});

onActivated(() => {
  // 当组件被激活时（从 keep-alive 缓存中恢复），重新初始化
  // 清理旧状态
  stopPolling();
  stopCountdown();
  qrCodeData.value = null;
  status.value = 'pending';
  userInfo.value = null;
  error.value = '';
  countdown.value = 0;
  // 重新获取二维码
  initQRCode();
});

onBeforeUnmount(() => {
  stopPolling();
  stopCountdown();
});

// 清理方法（用于切换登录方式时停止轮询）
const cleanup = () => {
  stopPolling();
  stopCountdown();
};

// 暴露方法给父组件
defineExpose({
  refresh: refreshQRCode,
  reset: initQRCode,
  cleanup: cleanup
});
</script>

<style scoped>
.wechat-login {
  text-align: center;
  padding: var(--tts-space-4);
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--tts-space-3);
}

.loading-icon {
  font-size: 24px;
  color: var(--tts-primary);
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.qr-code-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--tts-space-4);
}

.qr-code-wrapper {
  position: relative;
  border-radius: var(--tts-radius-base);
  overflow: hidden;
  box-shadow: var(--tts-shadow-base);
}

.qr-code {
  width: 200px;
  height: 200px;
  display: block;
}

.qr-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
}

.tip {
  color: var(--tts-text-secondary);
  font-size: var(--tts-font-sm);
  margin: 0;
}

.qr-actions {
  display: flex;
  gap: var(--tts-space-2);
}

.countdown {
  font-size: var(--tts-font-xs);
  color: var(--tts-text-tertiary);
}

.scanned-container,
.confirmed-container,
.expired-container,
.disabled-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--tts-space-3);
}

.success-icon {
  font-size: 48px;
  color: var(--tts-success);
}

.error-icon {
  font-size: 48px;
  color: var(--tts-error);
}

.disabled-container {
  padding: var(--tts-space-8) var(--tts-space-4);
}

.disabled-content {
  text-align: center;
  max-width: 320px;
  margin: 0 auto;
}

.disabled-icon {
  font-size: 64px;
  color: var(--tts-text-placeholder);
  margin-bottom: var(--tts-space-4);
}

.disabled-title {
  font-size: var(--tts-font-lg);
  font-weight: var(--tts-font-medium);
  color: var(--tts-text-primary);
  margin: 0 0 var(--tts-space-4) 0;
}

.disabled-desc {
  font-size: var(--tts-font-sm);
  color: var(--tts-text-secondary);
  line-height: 1.6;
  margin: 0 0 var(--tts-space-6) 0;
}

.disabled-actions {
  margin-bottom: var(--tts-space-4);
}

.disabled-actions .el-button {
  padding: 12px 24px;
  font-size: var(--tts-font-sm);
}

.disabled-footer {
  border-top: 1px solid var(--tts-border-light);
  padding-top: var(--tts-space-4);
}

.contact-info {
  font-size: var(--tts-font-xs);
  color: var(--tts-text-tertiary);
  margin: 0;
}

.status-text {
  font-size: var(--tts-font-base);
  font-weight: var(--tts-font-medium);
  margin: 0;
}

.user-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--tts-space-2);
  padding: var(--tts-space-4);
  background: var(--tts-bg-secondary);
  border-radius: var(--tts-radius-base);
  margin-top: var(--tts-space-2);
}

.nickname {
  font-size: var(--tts-font-sm);
  color: var(--tts-text-primary);
  margin: 0;
}

.error-container {
  width: 100%;
}
</style>
